"use client";
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { Button } from "@/app/components/shadcn/button";
import { Input } from "@/app/components/shadcn/input";
import { Badge } from "@/app/components/shadcn/badge";
import useFilters from "@/app/lib/features/filters/filters.hook";
import { GeneralFilterInput } from "@/app/lib/features/filters/filters.types";
import {
  formatPriceRangeLabel,
  getPriceBounds,
  isSamePriceRange,
  normalizePriceRange,
} from "@/app/utils/priceFilter.utils";

interface Props {
  filter: GeneralFilterInput;
}

const GeneralFilterPriceSection = ({ filter }: Props) => {
  const { selected, setPrice } = useFilters();
  const [isOpen, setIsOpen] = useState(false);
  const [minVal, setMinVal] = useState("");
  const [maxVal, setMaxVal] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const bounds = getPriceBounds(filter.min, filter.max);

  const hasValue =
    selected.price.min !== undefined || selected.price.max !== undefined;

  const displayLabel = formatPriceRangeLabel(selected.price, filter.title);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      zIndex: 9999,
    });
  };

  const handleOpen = () => {
    setMinVal(selected.price.min?.toString() ?? "");
    setMaxVal(selected.price.max?.toString() ?? "");
    updatePosition();
    setIsOpen(true);
  };

  const handleToggle = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      handleOpen();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => updatePosition();
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  // Girdiler yazarken serbest bırakılır; düzeltme blur'da ve Uygula'da yapılır.
  // Her tuşta kırpmak "1500" yazmak isteyen kullanıcıyı "1" → 29'a çeker.
  const syncInputsToNormalized = () => {
    const range = normalizePriceRange(minVal, maxVal, bounds);
    setMinVal(range.min !== undefined ? String(range.min) : "");
    setMaxVal(range.max !== undefined ? String(range.max) : "");
    return range;
  };

  const handleApply = () => {
    const range = syncInputsToNormalized();
    // Değişiklik yoksa Redux'a yazma — userInteractionCount artıp
    // gereksiz ürün fetch'i tetiklenmesin.
    if (!isSamePriceRange(range, selected.price)) setPrice(range);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    }
  };

  const handleClear = () => {
    setMinVal("");
    setMaxVal("");
    if (hasValue) setPrice({});
    setIsOpen(false);
  };

  const dropdown = (
    <div ref={dropdownRef} style={dropdownStyle} className="p-3 bg-white border rounded-md shadow-lg w-72 space-y-3">
      <p className="text-xs text-muted-foreground">{filter.description}</p>
      <div className="flex gap-2">
        <Input
          placeholder={`Min (${bounds.min})`}
          type="number"
          inputMode="numeric"
          className="h-8"
          value={minVal}
          min={bounds.min}
          max={bounds.max}
          onChange={(e) => setMinVal(e.target.value)}
          onBlur={syncInputsToNormalized}
          onKeyDown={handleKeyDown}
        />
        <Input
          placeholder={`Max (${bounds.max})`}
          type="number"
          inputMode="numeric"
          className="h-8"
          value={maxVal}
          min={bounds.min}
          max={bounds.max}
          onChange={(e) => setMaxVal(e.target.value)}
          onBlur={syncInputsToNormalized}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="flex-1 h-8" onClick={handleApply}>
          Uygula
        </Button>
        {hasValue && (
          <Button size="sm" variant="outline" className="flex-1 h-8" onClick={handleClear}>
            Temizle
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="relative">
      <Button
        ref={triggerRef}
        variant={hasValue ? "default" : "outline"}
        size="sm"
        className="h-9 gap-1"
        onClick={handleToggle}
      >
        <span>{displayLabel}</span>
        {hasValue && (
          <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">
            1
          </Badge>
        )}
        <ChevronDown
          size={14}
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </Button>
      {isOpen && typeof window !== "undefined" && createPortal(dropdown, document.body)}
    </div>
  );
};

export default GeneralFilterPriceSection;